import React from 'react';
import { AlertTriangle, CheckCircle2, ArrowDown } from 'lucide-react';
import { TEMPLATE_IMPACT_STAGE, OPPORTUNITY_LABELS, STAGE_BADGE_LABELS } from '../../lib/solutionStages';

// Micro chain — deliberately 4 nodes (no Learning) per spec: a tiny
// visual anchor, not another framework lesson.
const MICRO_CHAIN = ['capability', 'behavior', 'outcomes', 'financial'];

/**
 * SolutionOpportunityCard — "Current Highest Opportunity" panel above
 * the Quick-Start Templates. Surfaces the live Mission Control
 * constraint (chain stage + leak % + maturity-attenuated recovery),
 * a micro chain visual with the constraint highlighted, and the
 * templates whose Primary Impact Stage matches the constraint.
 * Self-hides when no chain stage declined this quarter.
 */
export default function SolutionOpportunityCard({
  leak, leakPct, recoveryPct, attenuated,
  templates = [], onTemplateSelect, loadingTemplate,
}) {
  if (!leak) return null;
  const recommended = templates.filter((t) => TEMPLATE_IMPACT_STAGE[t.id] === leak.key).slice(0, 3);

  return (
    <section
      className="rounded-2xl border border-orange-200 bg-gradient-to-r from-amber-50 via-orange-50/60 to-white p-5"
      data-testid="sf-opportunity-card"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_auto_1fr] gap-6 items-start">
        {/* Opportunity + numbers */}
        <div>
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 ring-1 ring-orange-200">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
            </span>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-orange-700">Current Highest Opportunity</p>
          </div>
          <p className="text-sm text-slate-600 mt-3">Your current strongest opportunity for value recovery is:</p>
          <p className="text-xl font-extrabold uppercase tracking-tight text-slate-900 mt-0.5" data-testid="sf-opportunity-stage">
            {OPPORTUNITY_LABELS[leak.key] || leak.stage.label}
          </p>
          <div className="flex items-center gap-8 mt-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Current Constraint</p>
              <p className="text-lg font-extrabold text-rose-600 tabular-nums" data-testid="sf-opportunity-constraint">−{leakPct}%</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Potential Recovery</p>
              <p
                className="text-lg font-extrabold text-emerald-600 tabular-nums"
                title={attenuated
                  ? `The ${leakPct}% leak attenuated by maturity realization — the defensible estimate, not the theoretical maximum.`
                  : 'Theoretical maximum — complete a Maturity Assessment to attenuate this into a defensible estimate.'}
                data-testid="sf-opportunity-recovery"
              >
                {attenuated ? `+${recoveryPct}%` : `up to +${recoveryPct}%`}
              </p>
            </div>
          </div>
        </div>

        {/* Micro chain visual — constraint stage highlighted */}
        <div className="hidden lg:flex flex-col items-center" data-testid="sf-micro-chain">
          {MICRO_CHAIN.map((k, i) => (
            <React.Fragment key={k}>
              <span
                className={`px-3 py-1 rounded-md text-[11px] font-bold border min-w-[96px] text-center ${
                  k === leak.key
                    ? 'bg-orange-100 text-orange-800 border-orange-300'
                    : 'bg-white text-slate-500 border-slate-200'
                }`}
              >
                {STAGE_BADGE_LABELS[k]}{k === leak.key ? ` −${leakPct}%` : ''}
              </span>
              {i < MICRO_CHAIN.length - 1 && <ArrowDown className="h-3 w-3 text-slate-300 my-0.5" />}
            </React.Fragment>
          ))}
        </div>

        {/* Recommended scenarios — templates matching the constraint stage */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {recommended.length ? 'Recommended Scenarios' : 'Recommended Focus'}
          </p>
          {recommended.length ? (
            <div className="mt-2 space-y-1.5">
              {recommended.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  disabled={loadingTemplate}
                  onClick={() => onTemplateSelect(t)}
                  className="w-full flex items-center gap-2 rounded-lg border border-orange-200 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-800 hover:border-orange-400 hover:bg-orange-50/50 transition-colors disabled:opacity-60"
                  data-testid={`sf-recommended-${t.id}`}
                >
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span className="truncate">{t.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-600 mt-2">
              {STAGE_BADGE_LABELS[leak.key] || leak.stage.label}-focused interventions — browse the templates below.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
